import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, ClipboardList, Zap, Plus, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import simulationService from '@/services/simulationService.ts';
import teamService from '@/services/teamService.ts';
import type { SimulationResponse } from '@/types/simulation.ts';
import SimulationCard from '@/components/simulation/SimulationCard.tsx';
import DeleteSimulationModal from '@/components/simulation/DeleteSimulationModal.tsx';
import QuickPracticeModal from '@/components/simulation/QuickPracticeModal.tsx';
import PastSessionsList from '@/components/simulation/PastSessionsList.tsx';
import GroupBySelector from '@/components/simulation/GroupBySelector.tsx';
import SortBySelector from '@/components/simulation/SortBySelector.tsx';
import { useGroupedSimulations } from '@/hooks/useGroupedSimulations.ts';
import type { GroupByKey } from '@/hooks/useGroupedSimulations.ts';
import { useSortedSimulations } from '@/hooks/useSortedSimulations.ts';
import type { SortConfig, SortByKey } from '@/hooks/useSortedSimulations.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { useAsyncData } from '@/hooks/useAsyncData.ts';
import { useDocumentProcessingPoller } from '@/hooks/useDocumentProcessingPoller.ts';

export default function SimulationList() {
  const { isSalesManager } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    data: simulations = [],
    loading,
    error,
    setData: setSimulations,
  } = useAsyncData<SimulationResponse[]>(
    () => (isSalesManager ? simulationService.getAll() : simulationService.getMyAssignments()),
    [isSalesManager],
    { initial: [], fallbackError: t('simulation.loadError') },
  );
  useDocumentProcessingPoller(simulations, (updated) => {
    setSimulations((prev) => (prev ?? []).map((s) => (s.id === updated.id ? updated : s)));
  });

  const [quickPracticeOpen, setQuickPracticeOpen] = useState(false);
  const [subTab, setSubTab] = useState<'configs' | 'sessions'>('configs');
  const [deleteTarget, setDeleteTarget] = useState<SimulationResponse | null>(null);
  const [duplicating, setDuplicating] = useState<string | null>(null);
  const [duplicatedSim, setDuplicatedSim] = useState<SimulationResponse | null>(null);
  // Per-tab sort & group state
  const [configGroupBy, setConfigGroupBy] = useState<GroupByKey>('none');
  const [configSort, setConfigSort] = useState<SortConfig>({ key: 'default', direction: 'asc' });
  const [sessionGroupBy, setSessionGroupBy] = useState<GroupByKey>('none');
  const [sessionSort, setSessionSort] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });

  // Derive current values from active tab
  const groupBy = subTab === 'configs' ? configGroupBy : sessionGroupBy;
  const setGroupBy = subTab === 'configs' ? setConfigGroupBy : setSessionGroupBy;
  const sort = subTab === 'configs' ? configSort : sessionSort;
  const setSort = subTab === 'configs' ? setConfigSort : setSessionSort;

  // Available options per tab + role
  const configGroupKeys: GroupByKey[] = isSalesManager
    ? ['none', 'simulationType', 'salePhase', 'sector', 'difficultyLevel', 'assignedSalespersonIds', 'maestro']
    : ['none', 'simulationType', 'salePhase', 'sector', 'difficultyLevel', 'maestro'];
  const sessionGroupKeys: GroupByKey[] = ['none', 'simulationType', 'difficultyLevel', 'maestro'];
  const configSortKeys: SortByKey[] = ['default', 'name', 'difficultyLevel', 'priceRange', 'createdAt'];
  const sessionSortKeys: SortByKey[] = ['default', 'name', 'difficultyLevel', 'createdAt'];

  const availableGroupKeys = subTab === 'configs' ? configGroupKeys : sessionGroupKeys;
  const availableSortKeys = subTab === 'configs' ? configSortKeys : sessionSortKeys;

  const [salespeopleMap, setSalespeopleMap] = useState<Map<string, string> | undefined>(undefined);

  // Load salespeople names when grouping by assignedSalespersonIds
  useEffect(() => {
    if (groupBy !== 'assignedSalespersonIds') return;
    if (salespeopleMap) return; // already loaded
    let cancelled = false;
    teamService.getSalespeople().then((people) => {
      if (!cancelled) {
        const map = new Map<string, string>();
        for (const p of people) {
          map.set(p.id, `${p.firstName} ${p.lastName}`);
        }
        setSalespeopleMap(map);
      }
    }).catch(() => { /* silent — groups will show IDs as fallback */ });
    return () => { cancelled = true; };
  }, [groupBy, salespeopleMap]);

  const sorted = useSortedSimulations(simulations, sort);

  // When the sort criterion matches the group criterion, reverse group order on desc
  const reverseGroups = sort.direction === 'desc' && groupBy === sort.key;
  const groups = useGroupedSimulations(sorted, groupBy, salespeopleMap, reverseGroups);

  const handleEdit = (sim: SimulationResponse) => {
    navigate(`/admin/simulations/edit/${sim.id}`);
  };

  const handleDuplicate = async (sim: SimulationResponse) => {
    setDuplicating(sim.id);
    try {
      type DuplicatePayload = Parameters<typeof simulationService.create>[0];
      const created = await simulationService.create({
        name: `${sim.name} ${t('simulation.copyNameSuffix')}`,
        simulationType: sim.simulationType as DuplicatePayload['simulationType'],
        salePhase: sim.salePhase as DuplicatePayload['salePhase'],
        objections: sim.objections,
        sector: sim.sector as DuplicatePayload['sector'],
        subSector: sim.subSector as DuplicatePayload['subSector'],
        priceRange: sim.priceRange as DuplicatePayload['priceRange'],
        difficultyLevel: sim.difficultyLevel,
        assignedMaestroId: sim.assignedMaestroId,
        assignedSalespersonIds: sim.assignedSalespersonIds,
        language: sim.language,
      });
      setSimulations((prev) => [...(prev ?? []), created]);
      // Small delay so the UI settles before showing the modal
      await new Promise((r) => setTimeout(r, 400));
      setDuplicating(null);
      setDuplicatedSim(created);
      return;
    } catch {
      // Silent fail — could add toast later
    }
    setDuplicating(null);
  };

  const handleDeleted = () => {
    if (deleteTarget) {
      setSimulations((prev) => (prev ?? []).filter((s) => s.id !== deleteTarget.id));
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="mt-3 text-sm">{t('simulation.loadingSimulations')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
        <AlertCircle className="h-8 w-8 text-red-500" />
        <p className="mt-3 text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (simulations.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <ClipboardList className="h-10 w-10 text-muted-foreground/40" />
          <p className="mt-3 text-sm font-medium text-foreground">
            {isSalesManager ? t('simulation.noSimulationsManager') : t('simulation.noSimulationsSalesperson')}
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            {isSalesManager
              ? t('simulation.noSimulationsManagerHint')
              : t('simulation.noSimulationsSalespersonHint')}
          </p>
          <div className="mt-4 flex gap-3">
            {isSalesManager && (
              <Button onClick={() => navigate('/admin/simulations/new')}>
                <Plus className="size-4" />
                {t('simulation.configureSimulation')}
              </Button>
            )}
            <Button variant="outline" onClick={() => setQuickPracticeOpen(true)}>
              <Zap className="size-4" />
              {t('simulation.quickPractice')}
            </Button>
          </div>
        </div>
        <QuickPracticeModal isOpen={quickPracticeOpen} onClose={() => setQuickPracticeOpen(false)} />
      </>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">
            {isSalesManager ? t('simulation.simulationsTitle') : t('simulation.mySimulationsTitle')}
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            {isSalesManager
              ? t('simulation.simulationsManagerDesc')
              : t('simulation.simulationsSalespersonDesc')}
          </p>
        </div>
        <div className="flex shrink-0 gap-2">
          {isSalesManager && (
            <Button onClick={() => navigate('/admin/simulations/new')}>
              <Plus className="size-4" />
              {t('simulation.configureSimulation')}
            </Button>
          )}
          <Button variant="outline" onClick={() => setQuickPracticeOpen(true)}>
            <Zap className="size-4" />
            {t('simulation.quickPractice')}
          </Button>
        </div>
      </div>

      {/* Sub-tab toggle + group selector */}
      <div className="mb-6 flex items-center gap-3">
        <div className="flex flex-1 gap-1 rounded-lg border border-border bg-muted p-1">
          <button
            onClick={() => setSubTab('configs')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              subTab === 'configs'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <ClipboardList className="h-4 w-4" />
            {t('simulation.tabConfigurations')}
          </button>
          <button
            onClick={() => setSubTab('sessions')}
            className={`flex flex-1 items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold transition-colors ${
              subTab === 'sessions'
                ? 'bg-card text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            <History className="h-4 w-4" />
            {t('simulation.tabPastSessions')}
          </button>
        </div>
        <SortBySelector value={sort} onChange={setSort} availableKeys={availableSortKeys} />
        <GroupBySelector value={groupBy} onChange={setGroupBy} availableKeys={availableGroupKeys} />
      </div>

      {subTab === 'configs' ? (
        <div className="space-y-6">
          {groups.map((group) => (
            <div key={group.key}>
              {groupBy !== 'none' && (
                <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                  {group.label}
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {group.simulations.length}
                  </span>
                </h3>
              )}
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {group.simulations.map((sim) => (
                  <div key={sim.id} className={duplicating === sim.id ? 'pointer-events-none opacity-50' : ''}>
                    <SimulationCard
                      simulation={sim}
                      showActions={isSalesManager}
                      onEdit={handleEdit}
                      onDuplicate={handleDuplicate}
                      onDelete={setDeleteTarget}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <PastSessionsList simulations={simulations} sort={sessionSort} groupBy={sessionGroupBy} />
      )}

      <QuickPracticeModal isOpen={quickPracticeOpen} onClose={() => setQuickPracticeOpen(false)} />

      <DeleteSimulationModal
        isOpen={!!deleteTarget}
        simulation={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onDeleted={handleDeleted}
      />

      {/* Duplicate success modal */}
      <Dialog open={!!duplicatedSim} onOpenChange={(open) => { if (!open) setDuplicatedSim(null); }}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('simulation.duplicatedTitle')}</DialogTitle>
            <DialogDescription>
              {duplicatedSim ? t('simulation.duplicatedMessage', { name: duplicatedSim.name }) : ''}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDuplicatedSim(null)}>
              {t('simulation.dontEdit')}
            </Button>
            <Button onClick={() => {
              if (!duplicatedSim) return;
              const id = duplicatedSim.id;
              setDuplicatedSim(null);
              navigate(`/admin/simulations/edit/${id}`);
            }}>
              {t('simulation.editNow')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
